import type { LessonState, LockMode } from "@canvaschool/contracts";

export type TeacherBarProps = {
  lesson: LessonState;
  onLock(mode: Exclude<LockMode, "OFF">, message: string): void;
  onUnlock(): void;
  /** Switches an individual canvas between focus mode (students stay in
   * their own frame) and help mode (students may help in others'). */
  onToggleFocusMode(): void;
  /** Whether focus mode (집중모드) is on. */
  focusMode?: boolean;
  /** False on a group canvas, which has no focus mode: the button shows
   * disabled, and a click still reports so the editor can say why. */
  focusModeAvailable?: boolean;
};

/**
 * The teacher's two always-visible lesson controls, sitting in the dock at
 * the bottom of the canvas. There is no "더보기" menu any more: the one
 * action it used to hold (학생 댓글 열기/잠그기) is gone with it, because
 * commenting is now decided per frame — only a sealed frame takes comments
 * — rather than by a lesson-wide switch.
 */
export const TeacherBar = ({
  lesson,
  onLock,
  onUnlock,
  onToggleFocusMode,
  focusMode = false,
  focusModeAvailable = true,
}: TeacherBarProps) => {
  const locked = lesson.lock.mode !== "OFF";

  return (
    <div className="canvaschool-teacher-bar">
      <button
        type="button"
        aria-pressed={locked}
        className={
          locked ? "canvaschool-dock-danger" : "canvaschool-dock-primary"
        }
        onClick={() => (locked ? onUnlock() : onLock("SCREEN", ""))}
      >
        {locked ? "화면 보여주기" : "화면 가리기"}
      </button>
      <button
        type="button"
        aria-pressed={focusModeAvailable && focusMode}
        aria-disabled={!focusModeAvailable}
        title={
          !focusModeAvailable
            ? "집중모드는 개인 템플릿에서만 사용할 수 있습니다."
            : focusMode
            ? "지금 집중모드: 학생은 자기 프레임에만 그립니다. 누르면 도움모드로 바뀝니다."
            : "지금 도움모드: 학생이 친구 프레임도 고칠 수 있습니다. 누르면 집중모드로 바뀝니다."
        }
        className={
          focusModeAvailable && focusMode ? "canvaschool-btn-active" : undefined
        }
        onClick={onToggleFocusMode}
      >
        {focusModeAvailable && !focusMode ? "도움모드" : "집중모드"}
      </button>
    </div>
  );
};
